import { type VariantProps, cva } from "class-variance-authority";
import { CreditCard, MoreVertical, Shield } from "lucide-react";
import * as React from "react";
import { Button } from "../atoms/button.js";
import { InlineIconButton } from "../atoms/inline-icon-button.js";
import { cx } from "../internal/cx.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu.js";
import {
  PaymentMethodCard,
  type PaymentMethodCardStatusBadge,
  type PaymentMethodCardType,
} from "./payment-method-card.js";

const billingOverviewPaymentMethodCardVariants = cva("cw-v2-billing-overview-payment-method-card", {
  variants: {
    variant: {
      default: "cw-v2-billing-overview-payment-method-card--default",
    },
    size: {
      md: "cw-v2-billing-overview-payment-method-card--md",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

export type BillingOverviewPaymentMethodType = PaymentMethodCardType;
export type BillingOverviewPaymentMethodBadgeType =
  | "verified"
  | "default"
  | "inactive"
  | "pending"
  | "expired";

export interface BillingOverviewPaymentMethodBadge {
  /** Badge meaning. `default` is shown as the primary chip. */
  type: BillingOverviewPaymentMethodBadgeType;
  /** Optional display label. Defaults from `type`. */
  label?: React.ReactNode;
}

export interface BillingOverviewPaymentMethod {
  /** Stable payment method id. */
  id: string;
  /** Payment method type. Drives the leading icon. */
  type?: BillingOverviewPaymentMethodType;
  /** Account name ("Operating checking", "Visa"). */
  name: React.ReactNode;
  /** Already-formatted masked account/card number. */
  number: React.ReactNode;
  /** Status badges attached to the method. */
  badges?: BillingOverviewPaymentMethodBadge[];
  /** Marks the method unavailable without removing its details. */
  disabled?: boolean;
  /** Optional reason rendered below the method number. */
  disabledReason?: React.ReactNode;
  /** Renders supporting copy in danger tone when true. */
  expired?: boolean;
  /**
   * Optional inline CTA (e.g. an "Enter deposit amounts" link) rendered under
   * the method details — forwarded to `PaymentMethodCard`'s `verificationLink`
   * slot. Apps own the action (routing/handlers); the design system only
   * renders whatever node is passed (hard rule #6).
   */
  verificationLink?: React.ReactNode;
}

export interface BillingOverviewPaymentMethodCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof billingOverviewPaymentMethodCardVariants> {
  /** Card eyebrow/title. Default "Payment method". */
  title?: React.ReactNode;
  /** Primary payment method to render. Omit or pass null for the empty state. */
  paymentMethod?: BillingOverviewPaymentMethod | null;
  /** Empty-state title. */
  emptyTitle?: React.ReactNode;
  /** Empty-state description. */
  emptyDescription?: React.ReactNode;
  /** Security note shown under the empty CTA. */
  emptySecurityNote?: React.ReactNode;
  /** Empty-state CTA label. */
  addActionLabel?: React.ReactNode;
  /** Accessible label for the empty-state CTA. */
  addActionAriaLabel?: string;
  /** Fires when the empty-state CTA is clicked. */
  onAddPaymentMethod?: () => void;
  /** Dropdown item label for managing billing. */
  manageBillingLabel?: React.ReactNode;
  /** Accessible label for the manage billing menu trigger. */
  manageBillingAriaLabel?: string;
  /** Shows a manage billing dropdown when a payment method is present. */
  onManageBilling?: () => void;
}

function getBadgeLabel(badge: BillingOverviewPaymentMethodBadge): React.ReactNode {
  if (badge.label) return badge.label;
  if (badge.type === "default") return "Active";
  return badge.type.charAt(0).toUpperCase() + badge.type.slice(1);
}

function getBadgeTone(
  type: BillingOverviewPaymentMethodBadgeType,
): PaymentMethodCardStatusBadge["tone"] {
  if (type === "verified" || type === "default") return "success";
  if (type === "expired") return "danger";
  if (type === "pending") return "warning";
  return "neutral";
}

function getStatusBadges(
  paymentMethod: BillingOverviewPaymentMethod,
): PaymentMethodCardStatusBadge[] {
  return (
    paymentMethod.badges
      ?.filter((badge) => badge.type !== "default")
      .map((badge) => ({
        id: `${paymentMethod.id}-${badge.type}-${String(getBadgeLabel(badge))}`,
        label: getBadgeLabel(badge),
        tone: getBadgeTone(badge.type),
      })) ?? []
  );
}

function getPrimaryBadge(paymentMethod: BillingOverviewPaymentMethod) {
  return paymentMethod.badges?.find((badge) => badge.type === "default");
}

/**
 * Billing Overview payment-method card. Renders the primary method when one is
 * present, otherwise a dedicated setup CTA card for first-time billing setup.
 */
export const BillingOverviewPaymentMethodCard = React.forwardRef<
  HTMLDivElement,
  BillingOverviewPaymentMethodCardProps
>(function BillingOverviewPaymentMethodCard(
  {
    addActionAriaLabel = "Set up payment method",
    addActionLabel = "Set up payment method",
    className,
    emptyDescription = "Securely store payment information for seamless billing.",
    emptySecurityNote = "Secured with bank-level encryption",
    emptyTitle = "No payment methods added yet",
    manageBillingAriaLabel = "Manage billing",
    manageBillingLabel = "Manage billing",
    onAddPaymentMethod,
    onManageBilling,
    paymentMethod,
    size,
    title = "Payment method",
    variant,
    ...props
  },
  ref,
) {
  const primaryBadge = paymentMethod ? getPrimaryBadge(paymentMethod) : undefined;

  return (
    <div
      ref={ref}
      className={cx(billingOverviewPaymentMethodCardVariants({ variant, size }), className)}
      {...props}
    >
      <div className="cw-v2-billing-overview-payment-method-card__header">
        <span className="cw-v2-billing-contact-card__eyebrow">{title}</span>
        {paymentMethod && onManageBilling ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <InlineIconButton
                aria-label={manageBillingAriaLabel}
                className="cw-v2-billing-overview-payment-method-card__menu-trigger"
                icon={<MoreVertical aria-hidden />}
                size="md"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onManageBilling}>
                <CreditCard aria-hidden size={16} />
                {manageBillingLabel}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      {paymentMethod ? (
        <PaymentMethodCard
          aria-disabled={paymentMethod.disabled || undefined}
          className={cx(
            "cw-v2-billing-overview-payment-method-card__method",
            paymentMethod.disabled &&
              "cw-v2-billing-overview-payment-method-card__method--disabled",
          )}
          name={paymentMethod.name}
          number={paymentMethod.number}
          primaryChipLabel={primaryBadge ? getBadgeLabel(primaryBadge) : undefined}
          showPrimaryChip={Boolean(primaryBadge)}
          statusBadges={getStatusBadges(paymentMethod)}
          supportingText={paymentMethod.disabledReason}
          supportingTextTone={paymentMethod.disabled || paymentMethod.expired ? "danger" : "muted"}
          type={paymentMethod.type}
          verificationLink={paymentMethod.verificationLink}
        />
      ) : (
        <div className="cw-v2-billing-overview-payment-method-card__empty">
          <div aria-hidden className="cw-v2-billing-overview-payment-method-card__empty-icon">
            <CreditCard size={18} />
          </div>
          <div className="cw-v2-billing-overview-payment-method-card__empty-body">
            <h3 className="cw-v2-billing-overview-payment-method-card__empty-title">
              {emptyTitle}
            </h3>
            {emptyDescription ? (
              <div className="cw-v2-billing-overview-payment-method-card__empty-description">
                {emptyDescription}
              </div>
            ) : null}
          </div>
          {onAddPaymentMethod ? (
            <div className="cw-v2-billing-overview-payment-method-card__empty-action">
              <Button
                aria-label={addActionAriaLabel}
                iconPrefix={<CreditCard aria-hidden size={16} />}
                onClick={onAddPaymentMethod}
                size="md"
              >
                {addActionLabel}
              </Button>
            </div>
          ) : null}
          {emptySecurityNote ? (
            <div className="cw-v2-billing-overview-payment-method-card__security-note">
              <Shield aria-hidden size={16} />
              {emptySecurityNote}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
});
BillingOverviewPaymentMethodCard.displayName = "BillingOverviewPaymentMethodCard";

export const V2BillingOverviewPaymentMethodCard = BillingOverviewPaymentMethodCard;
export type V2BillingOverviewPaymentMethodCardProps = BillingOverviewPaymentMethodCardProps;
export type V2BillingOverviewPaymentMethod = BillingOverviewPaymentMethod;
export type V2BillingOverviewPaymentMethodBadge = BillingOverviewPaymentMethodBadge;
export type V2BillingOverviewPaymentMethodBadgeType = BillingOverviewPaymentMethodBadgeType;
export type V2BillingOverviewPaymentMethodType = BillingOverviewPaymentMethodType;
